import { z } from 'zod';
/**
 * Reel storyboard (spec 100 section 4.1, S100-2). The body shapes of
 * `POST /v1/content-variants/{variant_id}/storyboard` and
 * `PUT /v1/storyboards/{id}` as zod, the limits and the total-duration rule the
 * editor shows live. Rules that need the database (asset usability, audio
 * license, the item's planned languages) live in `apps/api` (`StoryboardRules`).
 * Error messages of the refinements are the stable field codes (spec 100 section 5).
 */
export const REEL_VARIANT_FORMATS = ['ig_reel', 'fb_reel'];
/**
 * Variants that may carry a storyboard: the reels, and a story (Instagram, Facebook since S560-1), which then is
 * a video story (E490, S490-2): its render is published instead of the story's photos.
 */
export const STORYBOARD_VARIANT_FORMATS = ['ig_reel', 'fb_reel', 'ig_story', 'fb_story'];
export const isStoryboardFormat = (format) => STORYBOARD_VARIANT_FORMATS.includes(format);
/** A video story is 3-60 s (IG User Media reference, story video specification, read 2026-10-21). */
export const STORY_VIDEO_LIMITS = { totalMinMs: 3000, totalMaxMs: 60_000 };
export const REEL_MODES = ['storyboard', 'uploaded'];
export const REEL_TRANSITIONS = ['cut', 'crossfade', 'slide'];
export const REEL_TEXT_POSITIONS = ['top', 'center', 'bottom'];
/** `graphic` (E540): a scene without media on the brand background, drawn entirely by the composition. */
export const REEL_SCENE_KINDS = ['photo', 'video', 'graphic'];
/** The scene kinds with a media asset. */
export const REEL_MEDIA_SCENE_KINDS = ['photo', 'video'];
/** Scene layouts of composition v4 (spec 100 section 4.10, E540); `caption` is the text-over-footage of v1-v3. */
export const REEL_SCENE_LAYOUTS = ['caption', 'facts', 'steps', 'list', 'icon', 'portrait'];
/** Layouts that show `items`; every other layout refuses them. */
export const REEL_ITEM_LAYOUTS = ['facts', 'steps', 'list'];
export const layoutTakesItems = (layout) => REEL_ITEM_LAYOUTS.includes(layout);
/** How an animated photo scene moves over its duration. */
export const REEL_CAMERAS = ['zoom_in', 'zoom_out', 'pan_left', 'pan_right', 'still'];
/** How the texts and items of an animated scene appear. */
export const REEL_TEXT_ANIMATIONS = ['rise', 'pop', 'wipe', 'fade'];
/**
 * The fixed icon catalogue of animated scenes (ADR-039): kebab-case names of `lucide-react` line icons (ISC
 * licence). `packages/templates` maps each to its component; a name outside this list is `icon_unknown`.
 */
export const REEL_ICONS = [
    'activity',
    'award',
    'baby',
    'bed',
    'bike',
    'bus',
    'calendar',
    'camera',
    'car',
    'check',
    'clock',
    'coffee',
    'dumbbell',
    'euro',
    'footprints',
    'gift',
    'globe',
    'hammer',
    'hand-heart',
    'heart',
    'house',
    'info',
    'key',
    'leaf',
    'lightbulb',
    'mail',
    'map-pin',
    'message-circle',
    'mountain',
    'mountain-snow',
    'percent',
    'phone',
    'ruler',
    'shield-check',
    'smartphone',
    'snowflake',
    'sofa',
    'sparkles',
    'star',
    'stethoscope',
    'sun',
    'thumbs-up',
    'ticket',
    'trees',
    'user',
    'users',
    'utensils',
    'wifi',
];
export const isReelIcon = (name) => REEL_ICONS.includes(name);
/**
 * Limits (spec 100 section 4.1; the 3-90 s total is the common Meta range
 * verified 2026-09-29, the rest are proposals to confirm, section 9 Q3).
 */
export const REEL_LIMITS = {
    minScenes: 1,
    maxScenes: 12,
    /** Every scene, photo or trimmed video, lasts at least this long. */
    sceneMinMs: 1000,
    photoMaxMs: 10_000,
    outroMinMs: 1500,
    outroMaxMs: 4000,
    /** A `crossfade` or `slide` overlaps the two neighbouring scenes by this much. */
    transitionMs: 500,
    totalMinMs: 3000,
    totalMaxMs: 90_000,
    /** Scene and outro text, in code points. */
    textMaxChars: 60,
    /** The second, smaller line of a scene or item (E540). */
    subtextMaxChars: 90,
    /** Items of a `facts`, `steps` or `list` scene (E540). */
    maxItems: 4,
    audioFadeOutMaxMs: 10_000,
    /** Fade-in of the music at the start (S140-3). */
    audioFadeInMaxMs: 10_000,
    /** Crop aspect (width / height in pixels) of a scene or a cover photo. */
    cropAspect: 9 / 16,
    cropAspectTolerance: 0.02,
    /** Uploaded reels must be 9:16 within this relative tolerance. */
    uploadAspectTolerance: 0.01,
    /** An uploaded reel needs at least this resolution: the shorter side (720) and the longer side (1280), whatever the orientation (spec 100 section 9 Q4). */
    uploadMinShortSidePx: 720,
    uploadMinLongSidePx: 1280,
    /** Below this (shorter side 1080 or longer side 1920) an accepted upload only gets a hint: the render re-encodes it to 1080x1920. */
    uploadRecommendedShortSidePx: 1080,
    uploadRecommendedLongSidePx: 1920,
};
/** Longest total of a storyboard on this variant: a video story's 60 s, a reel's 90 s. */
export const storyboardMaxTotalMs = (format) => (format === 'ig_story' || format === 'fb_story' ? STORY_VIDEO_LIMITS.totalMaxMs : REEL_LIMITS.totalMaxMs);
/** True when a video of this size meets the minimum for an uploaded reel (shorter side >= 720 and longer side >= 1280). */
export const uploadResolutionOk = (width, height) => Math.min(width, height) >= REEL_LIMITS.uploadMinShortSidePx && Math.max(width, height) >= REEL_LIMITS.uploadMinLongSidePx;
/** True when an accepted upload is smaller than 1080x1920: the editor shows a non-blocking hint. */
export const uploadResolutionBelowRecommended = (width, height) => Math.min(width, height) < REEL_LIMITS.uploadRecommendedShortSidePx || Math.max(width, height) < REEL_LIMITS.uploadRecommendedLongSidePx;
/** Version of the reel composition in `packages/templates/src/reels/` a storyboard renders with (same rule as template versions).
 * v2 since S130-24 (readable scene text), v3 since S100-10 (gradient from the frame edge), v4 since E540 (layouts,
 * graphic scenes, motion); a storyboard saved earlier keeps its version until it is saved again. */
export const REEL_COMPOSITION_VERSION = 4;
/** Defaults of the E540 scene fields; scenes stored before E540 read with them. */
export const DEFAULT_REEL_SCENE_DESIGN = {
    layout: 'caption',
    subtexts: {},
    items: [],
    icon: null,
    camera: 'zoom_in',
    text_animation: 'rise',
};
export const DEFAULT_REEL_COVER = { source: 'frame', frame_ms: 0 };
export const DEFAULT_REEL_AUDIO = {
    start_offset_ms: 0,
    volume: 1,
    fade_in_ms: 0,
    fade_out_ms: 1000,
};
const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
/** True when the text has no emoji or other pictographic character. */
export const noEmoji = (text) => !PICTOGRAPHIC.test(text);
/** Length in code points (a precomposed letter counts once). */
export const charCount = (text) => Array.from(text).length;
/** Loose BCP-47 shape as a record key (or `mul`); the API checks the item's planned languages. */
const LANGUAGE_KEY = /^(mul|[a-z]{2,3}(-[A-Za-z0-9]{2,8})*)$/;
const EPSILON = 1e-9;
const textSchema = z
    .string()
    .refine((text) => charCount(text) <= REEL_LIMITS.textMaxChars, {
    message: 'too_long',
})
    .refine(noEmoji, { message: 'emoji_not_allowed' });
/** Overlay text per language; scenes and the outro may have none. */
const textsSchema = z.record(z.string().regex(LANGUAGE_KEY, { message: 'invalid_language' }), textSchema);
const subtextSchema = z
    .string()
    .refine((text) => charCount(text) <= REEL_LIMITS.subtextMaxChars, { message: 'too_long' })
    .refine(noEmoji, { message: 'emoji_not_allowed' });
const subtextsSchema = z.record(z.string().regex(LANGUAGE_KEY, { message: 'invalid_language' }), subtextSchema);
const iconSchema = z.string().refine(isReelIcon, { message: 'icon_unknown' });
/** One row of a `facts`, `steps` or `list` scene (E540). */
export const reelSceneItemInputSchema = z
    .object({
    icon: iconSchema.nullable().default(null),
    texts: textsSchema.default({}),
    subtexts: subtextsSchema.default({}),
})
    .strict();
const unit = z.number().min(0).max(1);
/** A normalized crop rectangle (relative to the original, like module 40's variant crops). */
export const reelCropSchema = z
    .object({ x: unit, y: unit, width: unit.gt(0), height: unit.gt(0) })
    .strict()
    .refine((crop) => crop.x + crop.width <= 1 + EPSILON && crop.y + crop.height <= 1 + EPSILON, { message: 'crop_out_of_bounds' });
const uuid = z.string().uuid();
const milliseconds = z.number().int();
export const reelSceneInputSchema = z
    .object({
    id: uuid.optional(),
    /** Required for a photo or video; a `graphic` scene has none (`null` or absent). */
    asset_id: uuid.nullable().optional(),
    kind: z.enum(REEL_SCENE_KINDS),
    trim_start_ms: milliseconds.nullable().optional(),
    trim_end_ms: milliseconds.nullable().optional(),
    duration_ms: milliseconds,
    crop: reelCropSchema.nullable().optional(),
    texts: textsSchema.default({}),
    text_position: z.enum(REEL_TEXT_POSITIONS).default('bottom'),
    transition: z.enum(REEL_TRANSITIONS).default('cut'),
    layout: z.enum(REEL_SCENE_LAYOUTS).default(DEFAULT_REEL_SCENE_DESIGN.layout),
    subtexts: subtextsSchema.default({}),
    items: z.array(reelSceneItemInputSchema).max(REEL_LIMITS.maxItems, { message: 'too_many_items' }).default([]),
    icon: iconSchema.nullable().default(null),
    camera: z.enum(REEL_CAMERAS).default(DEFAULT_REEL_SCENE_DESIGN.camera),
    text_animation: z.enum(REEL_TEXT_ANIMATIONS).default(DEFAULT_REEL_SCENE_DESIGN.text_animation),
})
    .strict();
export const reelOutroInputSchema = z
    .object({
    duration_ms: milliseconds,
    show_logo: z.boolean().default(true),
    texts: textsSchema.default({}),
})
    .strict();
export const reelAudioInputSchema = z
    .object({
    asset_id: uuid,
    start_offset_ms: milliseconds.default(DEFAULT_REEL_AUDIO.start_offset_ms),
    volume: z.number().min(0).max(1).default(DEFAULT_REEL_AUDIO.volume),
    fade_in_ms: milliseconds.default(DEFAULT_REEL_AUDIO.fade_in_ms),
    fade_out_ms: milliseconds.default(DEFAULT_REEL_AUDIO.fade_out_ms),
})
    .strict();
export const reelCoverInputSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('frame'), frame_ms: milliseconds }).strict(),
    z
        .object({
        source: z.literal('asset'),
        asset_id: uuid,
        crop: reelCropSchema.nullable().optional(),
    })
        .strict(),
]);
/** Body of a storyboard write in mode `storyboard`. */
export const storyboardModeInputSchema = z
    .object({
    mode: z.literal('storyboard').default('storyboard'),
    /** E540: render every scene and the outro frame by frame with motion; `false` keeps one still layer per scene. */
    animated: z.boolean().default(false),
    scenes: z.array(reelSceneInputSchema).min(REEL_LIMITS.minScenes, { message: 'required' }).max(REEL_LIMITS.maxScenes, { message: 'too_many_scenes' }),
    outro: reelOutroInputSchema,
    audio: reelAudioInputSchema.nullable().optional(),
    cover: reelCoverInputSchema.default(DEFAULT_REEL_COVER),
})
    .strict();
/** Body of a storyboard write in mode `uploaded`: a finished video asset of module 30. */
export const uploadedModeInputSchema = z
    .object({
    mode: z.literal('uploaded'),
    uploaded_asset_id: uuid,
    /** Must be `true` (checked by the API rules: `rights_not_confirmed`); a missing value is refused there, a non-boolean here. */
    rights_confirmed: z.boolean().optional(),
    cover: reelCoverInputSchema.default(DEFAULT_REEL_COVER),
})
    .strict();
/** Sum of the two overlaps a transition causes: none for a `cut`. */
export const transitionOverlapMs = (transition) => (transition === 'cut' ? 0 : REEL_LIMITS.transitionMs);
/**
 * Total duration of a storyboard reel in milliseconds (spec 100 section 4.1):
 * `sum(scene durations) + outro duration - sum(transition overlaps)`. Every
 * scene's transition leads into the next scene, the last scene's into the
 * outro, so every scene contributes its overlap.
 */
export function storyboardTotalDurationMs(scenes, outroDurationMs) {
    const scenesMs = scenes.reduce((sum, scene) => sum + scene.duration_ms, 0);
    const overlapMs = scenes.reduce((sum, scene) => sum + transitionOverlapMs(scene.transition), 0);
    return scenesMs + outroDurationMs - overlapMs;
}
//# sourceMappingURL=storyboard.js.map